/**
 * @file PoHubActions.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-06
 * @modified 2026-10-15
 * @description Divert and Split Order from the PO window. The customer order
 *              is the hub: both act on the PO's order (GET /api/orders/po-hub/:id
 *              names it and says whether each can be done — a stock PO, a
 *              Xero-made or archived PO, an invoiced order cannot), and the
 *              order's other POs follow exactly as they do from the order.
 *
 *              Three pieces, so PurchaseOrderModal needs only a few lines:
 *                usePoHub(poId)   the hub, and a reload
 *                <PoHubBadges>    Diverted / Split pills for the header
 *                <PoHubActions>   the two footer buttons and their flows
 *
 *              1.1.0 (2026-10-15, Cam): "Refused — redeliver…" / "Edit
 *              redelivery…" for the PO's order (RedeliverOrderModal,
 *              utils/redelivery) beside Divert, and the Redelivery pill.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config';
import { useSlideOutButton } from './SlideOutButton';
import DivertOrderFlow from './DivertOrderFlow';
import SplitOrderModal from './SplitOrderModal';
import SplitNotesModal from './SplitNotesModal';
import DivertedBadge from './DivertedBadge';
import RedeliveryBadge from './RedeliveryBadge';
import RedeliverOrderModal from './RedeliverOrderModal';
import { redeliverActionFor } from '../utils/redeliveryActions';
import './OrderHub.css';

/** The PO's hub order: { data: { po, order, divertBlock, splitBlock } | null, reload }. */
export function usePoHub(poId) {
  const [data, setData] = useState(null);
  const reload = useCallback(async () => {
    if (!poId) { setData(null); return; }
    try {
      const res = await fetch(`${API_URL}/api/orders/po-hub/${poId}`, { credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      setData(res.ok ? body : { order: null, divertBlock: body.error || 'Could not check', splitBlock: body.error || 'Could not check' });
    } catch (err) {
      setData({ order: null, divertBlock: err.message, splitBlock: err.message });
    }
  }, [poId]);
  useEffect(() => { reload(); }, [reload]);
  return { data, reload };
}

/** Diverted and Split pills for the PO window's header. */
export function PoHubBadges({ hub, onChanged }) {
  const [editingNotes, setEditingNotes] = useState(false);
  const order = hub?.data?.order;
  if (!order) return null;
  const inSplit = order.is_split || order.has_splits;
  const notes = order.is_split ? order.split_notes : order.child_split_notes;
  return (
    <>
      <DivertedBadge diversions={order.diversions} orderId={order.id} orderNumber={order.order_number} onChanged={onChanged} />
      <RedeliveryBadge redelivery={order.redelivery} orderId={order.id} onChanged={onChanged} />
      {inSplit && (
        <button
          type="button"
          className="split-badge"
          data-write-action
          title={`${order.order_number} ${order.is_split ? 'was split from its parent order' : 'has been split'}.`
            + `\n${notes ? `Split notes: ${notes}` : 'No split notes yet'}\nClick to add or change them.`}
          onClick={() => setEditingNotes(true)}
        >
          <span className="material-symbols-rounded">call_split</span>
          Split
        </button>
      )}
      {editingNotes && (
        <SplitNotesModal
          orderId={order.id}
          orderNumber={order.order_number}
          notes={notes || ''}
          onClose={() => setEditingNotes(false)}
          onSaved={onChanged}
        />
      )}
    </>
  );
}

/**
 * Footer buttons: Divert… and Split Order…, greyed with the reason when they
 * cannot be used — never hidden.
 * @param {object}   hub        usePoHub's result
 * @param {object}   user
 * @param {function} onChanged  After a divert or split — refresh the PO and what is behind.
 */
export function PoHubActions({ hub, user, onChanged }) {
  const [divertOrderId, setDivertOrderId] = useState(null);
  const [splitOrderId, setSplitOrderId] = useState(null);
  const [redeliverOrderId, setRedeliverOrderId] = useState(null);
  useSlideOutButton([hub?.data, divertOrderId, splitOrderId, redeliverOrderId]);

  const data = hub?.data;
  const loading = !data;
  const orderId = data?.order?.id || null;
  const divertBlock = loading ? 'Checking…' : data.divertBlock;
  const splitBlock = loading ? 'Checking…' : data.splitBlock;
  // Refused — redeliver…: the order's own rule, and who the user is.
  const redeliver = loading ? { mode: null, block: 'Checking…' }
    : (data.order ? redeliverActionFor(data.order, user)
      : { mode: null, block: data.redeliveryAction?.block || data.divertBlock });

  return (
    <>
      <button
        className="btn-slideout"
        data-write-action
        disabled={!!divertBlock}
        title={divertBlock || `Divert ${data.order.order_number} — the whole order, with this PO, to another customer`}
        onClick={() => { if (!divertBlock) setDivertOrderId(orderId); }}
      >
        <div className="icon-wrapper"><span className="material-symbols-rounded">alt_route</span></div>
        <span className="btn-label">Divert…</span>
      </button>
      <button
        className="btn-slideout"
        data-write-action
        disabled={!!splitBlock}
        title={splitBlock || `Split ${data.order.order_number} — part of it to other customers`}
        onClick={() => { if (!splitBlock) setSplitOrderId(orderId); }}
      >
        <div className="icon-wrapper"><span className="material-symbols-rounded">call_split</span></div>
        <span className="btn-label">Split Order…</span>
      </button>
      <button
        className="btn-slideout"
        data-write-action
        disabled={!!redeliver.block}
        title={redeliver.block || (redeliver.mode === 'edit'
          ? `Change ${data.order.order_number}'s redelivery — its date, where it is held, or cancel it`
          : `The customer refused ${data.order.order_number} — record where it is held and when it goes again (this PO stays as it is)`)}
        onClick={() => { if (!redeliver.block) setRedeliverOrderId(orderId); }}
      >
        <div className="icon-wrapper"><span className="material-symbols-rounded">{redeliver.mode === 'edit' ? 'edit_calendar' : 'assignment_return'}</span></div>
        <span className="btn-label">{redeliver.mode === 'edit' ? 'Edit redelivery…' : 'Refused — redeliver…'}</span>
      </button>

      {divertOrderId && (
        <DivertOrderFlow
          orderId={divertOrderId}
          user={user}
          onClose={() => { setDivertOrderId(null); hub.reload(); onChanged && onChanged(); }}
        />
      )}
      {redeliverOrderId && (
        <RedeliverOrderModal
          orderId={redeliverOrderId}
          onClose={() => { setRedeliverOrderId(null); hub.reload(); onChanged && onChanged(); }}
        />
      )}
      <SplitOrderModal
        isOpen={!!splitOrderId}
        orderId={splitOrderId}
        onClose={() => { setSplitOrderId(null); hub.reload(); onChanged && onChanged(); }}
        onSuccess={() => { setSplitOrderId(null); hub.reload(); onChanged && onChanged(); }}
      />
    </>
  );
}
